import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, FileText, Building2, Plus, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { apiService } from '@/lib/api';
import { money, LENDER_BLUE, LENDER_NAVY, getLenderNotifyEmail, setLenderNotifyEmail } from '@/lib/lenderDemo';

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

export default function LenderApply() {
  const navigate = useNavigate();
  const [docLibrary, setDocLibrary] = useState([]);
  const [form, setForm] = useState({ borrower_name: '', borrower_entity: '', project_name: '', address: '', lot_location: '', requested_amount: '' });
  const [bidLines, setBidLines] = useState([{ line: '', category: '', amount: '' }]);
  const [docs, setDocs] = useState({});
  const [notifyEmail, setNotifyEmail] = useState(getLenderNotifyEmail());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiService.lenderDemoProjects().then(({ data }) => {
      const lib = data.doc_library || [];
      setDocLibrary(lib);
      const d = {}; lib.slice(0, 3).forEach((x) => { d[x.key] = true; });
      setDocs(d);
    }).catch(() => {});
  }, []);

  const toggleDoc = (k) => setDocs((s) => ({ ...s, [k]: !s[k] }));
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const bidTotal = bidLines.reduce((t, l) => t + num(l.amount), 0);

  const submit = async () => {
    setErr('');
    if (!form.project_name || !form.borrower_name) { setErr('Enter the borrower and new project name.'); return; }
    const lines = bidLines.filter((l) => l.line && num(l.amount) > 0).map((l) => ({ line: l.line, category: l.category, amount: num(l.amount) }));
    if (lines.length === 0 && num(form.requested_amount) <= 0) { setErr('Add at least one bid-sheet line or a requested amount.'); return; }
    const documents = docLibrary.filter((x) => docs[x.key]).map((x) => ({ key: x.key, label: x.label, provided: true, filename: `${x.key}.pdf` }));
    const payload = {
      path: 'new', ...form,
      requested_amount: num(form.requested_amount) || bidTotal,
      bid_lines: lines, documents, notify_email: notifyEmail,
    };
    setBusy(true);
    try {
      const { data } = await apiService.lenderCreateApplication(payload);
      navigate(`/lender/application/${data.id}`);
    } catch {
      setErr('Could not submit the request. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F5F9' }}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ background: LENDER_BLUE }}>D</div>
            <span className="font-bold" style={{ color: LENDER_NAVY }}>DrawReady</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Borrower</span>
          </div>
          <button onClick={() => navigate('/lender/demo')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Lender demo
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6">
        <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>Submit a new project for funding</h1>
        <p className="text-slate-500 text-sm mb-5">Tell your lender about the project you want to build. Once they approve and you close, your funds are available to draw — app to app.</p>

        {/* Project details */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 space-y-3">
          <h3 className="font-semibold" style={{ color: LENDER_NAVY }}>Project & borrower</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="apply-borrower-name" value={form.borrower_name} onChange={(e) => setField('borrower_name', e.target.value)} placeholder="Borrower name" className="px-3 py-2 text-sm border border-slate-200 rounded-lg" />
            <input value={form.borrower_entity} onChange={(e) => setField('borrower_entity', e.target.value)} placeholder="Entity (LLC)" className="px-3 py-2 text-sm border border-slate-200 rounded-lg" />
            <input data-testid="apply-project-name" value={form.project_name} onChange={(e) => setField('project_name', e.target.value)} placeholder="New project name" className="px-3 py-2 text-sm border border-slate-200 rounded-lg" />
            <input value={form.address} onChange={(e) => setField('address', e.target.value)} placeholder="Property address" className="px-3 py-2 text-sm border border-slate-200 rounded-lg" />
            <input value={form.lot_location} onChange={(e) => setField('lot_location', e.target.value)} placeholder="Lot / legal description" className="px-3 py-2 text-sm border border-slate-200 rounded-lg" />
            <input data-testid="apply-requested-amount" value={form.requested_amount} onChange={(e) => setField('requested_amount', e.target.value)} placeholder="Requested loan amount" type="number" className="px-3 py-2 text-sm border border-slate-200 rounded-lg" />
          </div>
        </div>

        {/* Bid sheet */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="font-semibold" style={{ color: LENDER_NAVY }}>Bid sheet (schedule of values)</h3>
            <span className="text-sm font-semibold" style={{ color: LENDER_BLUE }}>Total {money(bidTotal)}</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">List the budget for this project. Your lender reviews it and DrawReady maps each line to the right construction phase at closing.</p>
          {bidLines.map((l, i) => (
            <div key={i} className="flex gap-2 mb-1.5" data-testid={`apply-bidline-${i}`}>
              <input value={l.line} onChange={(e) => setBidLines((s) => s.map((x, j) => j === i ? { ...x, line: e.target.value } : x))} placeholder="Line item (e.g. Framing)" className="flex-1 px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg" />
              <input value={l.category} onChange={(e) => setBidLines((s) => s.map((x, j) => j === i ? { ...x, category: e.target.value } : x))} placeholder="Category" className="w-32 px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg" />
              <input value={l.amount} onChange={(e) => setBidLines((s) => s.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} placeholder="$" type="number" className="w-28 px-2.5 py-1.5 text-sm text-right border border-slate-200 rounded-lg" />
              <button onClick={() => setBidLines((s) => s.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex items-center justify-between mt-2">
            <button onClick={() => setBidLines((s) => [...s, { line: '', category: '', amount: '' }])} data-testid="apply-add-line" className="flex items-center gap-1 text-xs font-medium" style={{ color: LENDER_BLUE }}><Plus className="w-3.5 h-3.5" /> Add line</button>
            <span className="text-[11px] text-slate-400">Coming soon: paste from a spreadsheet, or fill out your lender's sworn statement</span>
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
          <h3 className="font-semibold mb-1 flex items-center gap-1.5" style={{ color: LENDER_NAVY }}><FileText className="w-4 h-4" /> Documents you're attaching</h3>
          <p className="text-xs text-slate-500 mb-3">Everything a lender typically needs. They can request more after review.</p>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {docLibrary.map((x) => (
              <button key={x.key} onClick={() => toggleDoc(x.key)} data-testid={`apply-doc-${x.key}`} className="flex items-center gap-2 text-sm text-left">
                {docs[x.key] ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
                <span className={docs[x.key] ? 'text-slate-700' : 'text-slate-500'}>{x.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <label className="block text-xs font-medium text-slate-500 mb-1">Notify me / the lender at <span className="text-slate-400">(demo)</span></label>
          <input type="email" value={notifyEmail} data-testid="apply-notify-email" onChange={(e) => { setNotifyEmail(e.target.value); setLenderNotifyEmail(e.target.value); }} placeholder="you@email.com" className="w-full mb-3 px-3 py-2 text-sm border border-slate-200 rounded-lg" />
          {err && <p className="text-xs text-red-500 mb-2" data-testid="apply-error">{err}</p>}
          <button onClick={submit} disabled={busy} data-testid="apply-submit"
            className="flex items-center justify-center gap-1.5 w-full py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-60" style={{ background: LENDER_BLUE }}>
            <Send className="w-4 h-4" /> {busy ? 'Submitting…' : 'Submit loan request'}
          </button>
          <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Building2 className="w-3 h-3" /> Goes straight to the lender on DrawReady — no email or PDF.</p>
        </div>
      </main>
    </div>
  );
}
